"use client";

import { Container, Heading, Image, Text } from "@pamperedchef/design-system";

import {
	FeaturedText,
	type FeaturedTextProps,
	type RichTextHeadingTag
} from "@/composites/foundation";

import * as styles from "./CategoryBanner.module.scss";

export interface CategoryBannerImage {
	src: string;
	alt: string;
}

export type CategoryBannerKind = "category" | "subcategory";

export interface CategoryBannerHero {
	eyebrow?: string;
	headline: string;
	headlineTag?: RichTextHeadingTag;
	headlineStyle?: FeaturedTextProps["styledAs"];
	theme?: FeaturedTextProps["theme"];
	description?: string;
	image?: CategoryBannerImage;
}

export interface CategoryBannerDerivedProps {
	variant?: "derived";
	kind?: CategoryBannerKind;
	eyebrow?: string;
	headline: string;
	description?: string;
	image?: CategoryBannerImage;
}

export interface CategoryBannerHeroProps extends CategoryBannerHero {
	variant: "hero";
}

export type CategoryBannerProps =
	CategoryBannerDerivedProps | CategoryBannerHeroProps;

function cx(...classes: Array<string | false | undefined>): string {
	return classes.filter(Boolean).join(" ");
}

function Description({ text }: { text?: string }): JSX.Element | null {
	return text ? (
		<Text tag="p" className={styles["banner__description"]}>
			{text}
		</Text>
	) : null;
}

function Eyebrow({ text }: { text?: string }): JSX.Element | null {
	return text ? (
		<Text tag="p" className={styles["banner__eyebrow"]}>
			{text}
		</Text>
	) : null;
}

function DerivedCopy({
	eyebrow,
	headline,
	description
}: CategoryBannerDerivedProps): JSX.Element {
	return (
		<>
			<div className={styles["banner__header"]}>
				<Eyebrow text={eyebrow} />
				<Heading tag="h1" className={styles["banner__headline"]}>
					{headline}
				</Heading>
			</div>
			<Description text={description} />
		</>
	);
}

function HeroCopy({
	eyebrow,
	headline,
	headlineTag = "h1",
	headlineStyle = "display-md",
	theme,
	description
}: CategoryBannerHeroProps): JSX.Element {
	return (
		<>
			<div className={styles["banner__header"]}>
				<Eyebrow text={eyebrow} />
				<FeaturedText
					className={cx(
						styles["banner__hero"],
						styles[`banner__hero--${headlineStyle}`]
					)}
					headline={headline}
					headingTag={headlineTag}
					styledAs={headlineStyle}
					theme={theme}
					alignment="inherit"
				/>
			</div>
			<Description text={description} />
		</>
	);
}

function Art({ image }: { image: CategoryBannerImage }): JSX.Element {
	return (
		<div className={styles["banner__art"]}>
			<div className={styles["banner__canvas"]}>
				<span
					aria-hidden="true"
					className={cx(
						styles["banner__line"],
						styles["banner__line--back"]
					)}
				/>
				<Image
					src={image.src}
					alt={image.alt}
					className={styles["banner__image"]}
				/>
				<span
					aria-hidden="true"
					className={cx(
						styles["banner__line"],
						styles["banner__line--front"]
					)}
				/>
			</div>
		</div>
	);
}

export function CategoryBanner(props: CategoryBannerProps): JSX.Element {
	const variant = props.variant ?? "derived";
	const kind: CategoryBannerKind =
		props.variant === "hero" ? "category" : (props.kind ?? "category");
	const { image } = props;

	return (
		<section
			className={cx(
				styles["banner"],
				styles[`banner--${kind}`],
				image && styles["banner--with-image"]
			)}
			data-component="CategoryBanner"
			data-variant={variant}
			data-kind={kind}
			data-has-image={image ? "true" : "false"}
		>
			<Container>
				<div className={styles["banner__inner"]}>
					<div className={styles["banner__copy"]}>
						{props.variant === "hero" ? (
							<HeroCopy {...props} />
						) : (
							<DerivedCopy {...props} />
						)}
					</div>

					{image ? <Art image={image} /> : null}
				</div>
			</Container>
		</section>
	);
}
